import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from './ui';

/**
 * IF A PAGE CRASHES, SAY SO — owner, 2026-10-09. A throw while drawing used to blank the whole app
 * (switching tables on the score sheet did it on staging). This catches it inside the normal frame, so
 * the menu still works, and offers one way out. The wording is the owner's: one line and a button.
 *
 * Keyed by the address, so moving to another page clears it. The error goes to the browser console
 * only: the web app has no error reporting of its own yet.
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <Boundary key={location.pathname}>{children}</Boundary>;
}

class Boundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    console.error('A page crashed while drawing', error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="mx-auto flex max-w-xl flex-col items-start gap-4 px-6 py-12">
        <p className="font-display text-xl">Something went wrong on this page.</p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </div>
    );
  }
}
